import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  lazy,
  Suspense,
  type ReactNode,
  type MouseEvent,
} from "react";
import { QueryClientProvider } from "@tanstack/react-query";

import {
  api,
  ApiRequestError,
  onSessionExpired,
  resetSessionExpiration,
  type Session,
} from "./api.js";
import { AdminPage } from "./pages/admin.js";
import { FinanceWorkspace } from "./pages/finance-workspace.js";
import { OpenProjectPage } from "./pages/openproject.js";
import { Notice, failure } from "./shared/ui.js";
import { PayrollAuthProvider } from "./payroll/app/auth.js";
import { PayrollAuditOverlay } from "./payroll/components/payroll/PayrollAuditOverlay.js";
import {
  Avatar as HeroAvatar,
  Button as HeroButton,
} from "./payroll/components/ui/index.js";
import { queryClient as payrollQueryClient } from "./payroll/lib/queryClient.js";
import {
  PayrollAdminPage,
  PayrollPeriodSummaryPage,
} from "./payroll/routes/payroll.js";
import {
  PayrollEmployeeProfilePage,
  PayrollProfilesPage,
} from "./payroll/routes/payrollProfiles.js";

const SystemActivity = lazy(async () => ({
  default: (await import("./components/system-activity.js")).SystemActivity,
}));

type Page =
  | "Payroll"
  | "Invoice scanning"
  | "Work Profiles"
  | "Period Summary"
  | "Settings"
  | "OpenProject Sync"
  | "Access & security";

type IconName = "logout" | "settings" | "activity" | "close";

interface NavigationItem {
  page: Page;
  path: string;
}

const NAVIGATION: readonly NavigationItem[] = [
  {
    page: "Payroll",
    path: "/",
  },
  {
    page: "Invoice scanning",
    path: "/invoice-scanning",
  },
  {
    page: "Work Profiles",
    path: "/work-profiles",
  },
  {
    page: "Period Summary",
    path: "/period-summary",
  },
  {
    page: "Settings",
    path: "/settings",
  },
  {
    page: "OpenProject Sync",
    path: "/openproject-sync",
  },
  {
    page: "Access & security",
    path: "/administration",
  },
] as const;

const SETTINGS_PAGES = new Set<Page>([
  "Settings",
  "OpenProject Sync",
  "Access & security",
]);
const SETTINGS_OWNER_PAGES = new Set<Page>([
  "OpenProject Sync",
  "Access & security",
]);
const PAYROLL_QUERY_KEYS = [
  "month",
  "stage",
  "payslipStep",
  "periodThrough",
] as const;
const pageFromPath = (): Page =>
  NAVIGATION.find(({ path }) =>
    path === "/"
      ? location.pathname === "/" || location.pathname === "/payroll"
      : location.pathname.startsWith(path),
  )?.page ?? "Payroll";

const initials = (name: string | null) =>
  (name ?? "PayDay user")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

function authentikAccountUrl(issuer: string): string | null {
  try {
    const url = new URL("/if/user/", issuer);
    return url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
}

function Icon({ name, size = 19 }: { name: IconName; size?: number }) {
  const paths: Record<IconName, ReactNode> = {
    logout: (
      <>
        <path d="M10 5H5v14h5M14 16l4-4-4-4M18 12H9" />
      </>
    ),
    settings: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1V21h-4v-.08a1.7 1.7 0 0 0-1.1-1.55 1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1-.4H3v-4h.08a1.7 1.7 0 0 0 1.55-1.1 1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1V3h4v.08a1.7 1.7 0 0 0 1.1 1.55 1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 9c.14.37.35.7.6 1 .26.29.62.43 1 .4h.08v4H21c-.38-.03-.74.11-1 .4-.25.3-.46.63-.6 1Z" />
      </>
    ),
    activity: (
      <>
        <path d="M6 3h8l4 4v14H6z" />
        <path d="M14 3v5h5M9 12h6M9 16h6" />
      </>
    ),
    close: <path d="m6 6 12 12M18 6 6 18" />,
  };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

export function App() {
  const [session, setSession] = useState<Session | null>();
  const [error, setError] = useState<unknown>();
  const [page, setPage] = useState<Page>(pageFromPath);
  const [pathname, setPathname] = useState(() => window.location.pathname);
  const [navigationSearch, setNavigationSearch] = useState(
    () => window.location.search,
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const settingsTriggerRef = useRef<HTMLButtonElement>(null);
  const activityTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (
      page === "Payroll" ||
      page === "Period Summary" ||
      page === "Work Profiles"
    )
      return;
    const url = new URL(window.location.href);
    for (const key of PAYROLL_QUERY_KEYS) url.searchParams.delete(key);
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, [page, pathname]);

  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
    settingsTriggerRef.current?.focus();
  }, []);
  const closeActivity = useCallback(() => {
    setActivityOpen(false);
    activityTriggerRef.current?.focus();
  }, []);

  useEffect(() => {
    resetSessionExpiration();
    void api
      .session()
      .then(setSession)
      .catch((value: unknown) => {
        if (value instanceof ApiRequestError && value.status === 401)
          setSession(null);
        else setError(value);
      });
  }, []);

  useEffect(() => {
    const expire = () => {
      void payrollQueryClient.cancelQueries();
      setSession(null);
    };
    const unsubscribe = onSessionExpired(expire);
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset["theme"] = "light";
    root.dataset["accent"] = "teal";
    root.style.colorScheme = "light";
  }, []);

  useEffect(() => {
    const onPayrollContextChange = () =>
      setNavigationSearch(window.location.search);
    const onPopState = () => {
      setPathname(window.location.pathname);
      setPage(pageFromPath());
      onPayrollContextChange();
    };
    window.addEventListener("popstate", onPopState);
    window.addEventListener("payroll-context-change", onPayrollContextChange);
    return () => {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener(
        "payroll-context-change",
        onPayrollContextChange,
      );
    };
  }, []);

  const permittedNavigation = useMemo(() => {
    if (session === null || session === undefined) return [];
    return [...NAVIGATION];
  }, [session]);
  const moduleNavigation = useMemo(
    () =>
      permittedNavigation.filter(({ page }) => !SETTINGS_OWNER_PAGES.has(page)),
    [permittedNavigation],
  );

  if (error)
    return (
      <main className="center">
        <Notice error>{failure(error)}</Notice>
        <button
          onClick={() => {
            location.reload();
          }}
        >
          Try again
        </button>
      </main>
    );
  if (session === undefined)
    return (
      <main className="center session-check">
        <div className="session-check-mark" aria-hidden="true" />
        <p>Checking your secure PayDay session…</p>
      </main>
    );
  if (session === null) return <Login />;

  const permittedPage = permittedNavigation.some((item) => item.page === page)
    ? page
    : permittedNavigation[0]?.page;
  const settingsNavigation = permittedNavigation.filter(({ page: itemPage }) =>
    SETTINGS_OWNER_PAGES.has(itemPage),
  );
  const currentModulePage =
    permittedPage !== undefined && SETTINGS_PAGES.has(permittedPage)
      ? "Settings"
      : permittedPage;
  const activeSettingsPage =
    permittedPage === "Settings" ? settingsNavigation[0]?.page : permittedPage;
  const displayName = session.principal.displayName ?? "PayDay user";
  const navigationHref = (item: NavigationItem) => {
    if (permittedPage !== "Payroll" || item.page !== "Payroll")
      return item.path;
    const current = new URLSearchParams(navigationSearch);
    const own = new URLSearchParams();
    for (const key of PAYROLL_QUERY_KEYS) {
      const value = current.get(key);
      if (value !== null) own.set(key, value);
    }
    return own.size ? `${item.path}?${own.toString()}` : item.path;
  };
  const navigate = (item: NavigationItem) => {
    window.history.pushState({}, "", navigationHref(item));
    setPathname(item.path);
    setPage(item.page);
  };

  return (
    <div className="shell fiori-floorplan-shell" data-layout="fiori-floorplan">
      <a className="skip" href="#content">
        Skip to main content
      </a>
      <header className="fiori-topbar" aria-label="PayDay application">
        <div className="fiori-topbar__brand-group">
          <HeroButton
            type="button"
            variant="ghost"
            className="fiori-brand-button"
            aria-label="Go to Payroll"
            onClick={() => {
              const payroll = permittedNavigation.find(
                ({ page: itemPage }) => itemPage === "Payroll",
              );
              if (payroll !== undefined) navigate(payroll);
            }}
          >
            <img src="/payday-coin-stack.svg" alt="" aria-hidden="true" />
            <strong>PayDay</strong>
          </HeroButton>
        </div>
        <nav
          className="fiori-workspace-navigation"
          aria-label="PayDay workspaces"
        >
          {moduleNavigation.map((item) => (
            <a
              key={item.page}
              href={navigationHref(item)}
              aria-current={
                currentModulePage === item.page ? "page" : undefined
              }
              data-control-id={`navigation.${item.page.toLowerCase().replaceAll(" ", "-")}`}
              onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                if (
                  event.button !== 0 ||
                  event.ctrlKey ||
                  event.metaKey ||
                  event.shiftKey ||
                  event.altKey
                )
                  return;
                event.preventDefault();
                navigate(item);
              }}
              onFocus={(event) => {
                const link = event.currentTarget;
                const nav = link.parentElement;
                if (!nav) return;
                const bounds = link.getBoundingClientRect();
                const visible = nav.getBoundingClientRect();
                if (bounds.left < visible.left)
                  nav.scrollLeft += bounds.left - visible.left;
                else if (bounds.right > visible.right)
                  nav.scrollLeft += bounds.right - visible.right;
              }}
            >
              {item.page}
            </a>
          ))}
        </nav>

        <div className="fiori-topbar__actions" aria-label="Account actions">
          <HeroButton
            ref={activityTriggerRef}
            variant="ghost"
            iconOnly
            data-control-id="diagnostics.open-activity"
            type="button"
            aria-label="Open system activity"
            title="System activity"
            onClick={() => {
              setSettingsOpen(false);
              setActivityOpen(true);
            }}
            className="fiori-icon-button"
          >
            <Icon name="activity" size={17} />
          </HeroButton>
          <HeroButton
            ref={settingsTriggerRef}
            variant="ghost"
            iconOnly
            type="button"
            data-control-id="settings.open"
            aria-label="Open settings"
            title="Account settings"
            onClick={() => {
              setActivityOpen(false);
              setSettingsOpen(true);
            }}
            className="fiori-icon-button"
          >
            <Icon name="settings" size={17} />
          </HeroButton>
          <div
            className="fiori-user"
            aria-label={`Signed in as ${displayName}`}
          >
            <span className="fiori-user__copy">
              <strong>{displayName}</strong>
            </span>
            <HeroAvatar size="sm" initials={initials(displayName)} />
          </div>
          <HeroButton
            variant="ghost"
            iconOnly
            className="fiori-icon-button"
            type="button"
            data-control-id="session.sign-out"
            aria-label="Sign out"
            title="Sign out"
            onClick={() => {
              void api
                .logout()
                .then(() => {
                  setSession(null);
                })
                .catch(setError);
            }}
          >
            <Icon name="logout" size={17} />
          </HeroButton>
        </div>
      </header>

      {settingsOpen && (
        <SettingsDialog session={session} onClose={closeSettings} />
      )}
      {activityOpen && (
        <Suspense fallback={null}>
          <SystemActivity onClose={closeActivity} />
        </Suspense>
      )}

      <div className="workspace">
        <main
          id="content"
          tabIndex={-1}
          className="workspace-main"
          data-page={permittedPage?.toLowerCase().replaceAll(" ", "-")}
        >
          {permittedPage === undefined ? (
            <section className="access-empty">
              <h1>PayDay access is not assigned</h1>
              <p>
                Your Authentik identity is valid, but this PayDay instance has
                not granted you access yet.
              </p>
            </section>
          ) : (
            <>
              {permittedPage === "Invoice scanning" && <FinanceWorkspace />}
              <QueryClientProvider client={payrollQueryClient}>
                <PayrollAuthProvider session={session}>
                  <div className="payroll-host">
                    {permittedPage === "Payroll" && <PayrollAdminPage />}
                    {permittedPage === "Work Profiles" &&
                      (/^\/work-profiles\/[^/]+$/u.test(pathname) ? (
                        <PayrollEmployeeProfilePage key={pathname} />
                      ) : (
                        <PayrollProfilesPage key={pathname} />
                      ))}
                    {permittedPage === "Period Summary" && (
                      <PayrollPeriodSummaryPage />
                    )}
                    <PayrollAuditOverlay pathname={pathname} />
                  </div>
                </PayrollAuthProvider>
              </QueryClientProvider>
              {SETTINGS_PAGES.has(permittedPage) && (
                <SettingsWorkspace
                  activePage={activeSettingsPage}
                  navigation={settingsNavigation}
                  onNavigate={navigate}
                >
                  {activeSettingsPage === "OpenProject Sync" && (
                    <OpenProjectPage session={session} />
                  )}
                  {activeSettingsPage === "Access & security" && (
                    <AdminPage session={session} />
                  )}
                </SettingsWorkspace>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

const SETTINGS_COPY: Record<
  Extract<Page, "OpenProject Sync" | "Access & security">,
  { title: string; description: string }
> = {
  "OpenProject Sync": {
    title: "OpenProject Sync",
    description:
      "Read-only source connection, synchronization, and run history.",
  },
  "Access & security": {
    title: "People & sessions",
    description: "Observed identities and active PayDay sessions.",
  },
};

function SettingsWorkspace({
  activePage,
  navigation,
  onNavigate,
  children,
}: {
  activePage: Page | undefined;
  navigation: readonly NavigationItem[];
  onNavigate: (item: NavigationItem) => void;
  children?: ReactNode;
}) {
  return (
    <section
      className="settings-workspace"
      data-settings-area={activePage?.toLowerCase().replaceAll(" ", "-")}
    >
      <h1 className="sr-only">Settings</h1>
      <nav className="settings-route-navigation" aria-label="Settings areas">
        {navigation.map((item) => (
          <button
            key={item.page}
            type="button"
            aria-current={activePage === item.page ? "page" : undefined}
            onClick={() => {
              onNavigate(item);
            }}
          >
            {SETTINGS_COPY[item.page as keyof typeof SETTINGS_COPY].title}
          </button>
        ))}
      </nav>
      {activePage === undefined ? (
        <div className="settings-empty">
          No operational settings are assigned to this account.
        </div>
      ) : (
        children
      )}
    </section>
  );
}

function SettingsDialog({
  session,
  onClose,
}: {
  session: Session;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const displayName = session.principal.displayName ?? "PayDay user";
  const accountUrl = authentikAccountUrl(session.principal.issuer);

  return (
    <div
      className="settings-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={dialogRef}
        className="settings-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
        aria-describedby="settings-description"
      >
        <header className="settings-header">
          <div>
            <h2 id="settings-title">Settings</h2>
            <p id="settings-description">
              View your account and sign-in details.
            </p>
          </div>
          <button
            ref={closeRef}
            className="icon-button settings-close"
            type="button"
            aria-label="Close settings"
            onClick={onClose}
          >
            <Icon name="close" size={18} />
          </button>
        </header>

        <div className="settings-scroll">
          <section className="settings-section account-settings">
            <h3>Account</h3>
            <div className="settings-account-row">
              <span className="avatar">{initials(displayName)}</span>
              <span>
                <strong>{displayName}</strong>
              </span>
              {accountUrl !== null && (
                <a
                  className="settings-account-link"
                  href={accountUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open Authentik
                </a>
              )}
            </div>
            <p>
              Authentik manages sign-in and identity. Anyone who can sign in to
              this PayDay application can use its pages.
            </p>
          </section>
        </div>
      </section>
    </div>
  );
}

export function Login({
  initialError,
  initialLoading = false,
}: {
  initialError?: unknown;
  initialLoading?: boolean;
} = {}) {
  const [error, setError] = useState<unknown>(initialError);
  const [loading, setLoading] = useState(initialLoading);

  const beginSignIn = async () => {
    setLoading(true);
    setError(undefined);
    try {
      const { authorizationUrl } = await api.login();
      window.location.assign(authorizationUrl);
    } catch (caught) {
      setError(caught);
      setLoading(false);
    }
  };

  return (
    <main className="login-page" data-login-surface>
      <section aria-labelledby="login-heading" className="login-panel">
        <header className="login-header">
          <p className="login-brand-name">Consultis</p>
          <h1 id="login-heading" className="login-title">
            Sign in
          </h1>
          <p className="login-description">
            Continue securely with Authentik to open PayDay.
          </p>
        </header>
        <div className="login-content">
          {error !== undefined && (
            <div className="login-error" role="alert">
              {failure(error)}
            </div>
          )}
          <button
            aria-label="Continue with Authentik"
            className="login-action"
            data-disabled={loading || undefined}
            data-pending={loading || undefined}
            disabled={loading}
            onClick={() => void beginSignIn()}
            type="button"
          >
            {loading ? "Opening Authentik…" : "Continue with Authentik"}
          </button>
        </div>
      </section>
    </main>
  );
}
